import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Group,
  NativeSelect,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconDownload } from '@/components/icons'
import { api, ApiError } from '@/lib/api'
import { downloadBlob } from '@/lib/download-blob'
import { ExportLimitField, ExportPreviewNote, useExportPreview } from '@/components/exports/ExportLimitControls'
import type { ChannelRow, ProjectRow } from '@/types/api'

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10)
}

function daysAgoIsoDate(days: number) {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

function safeFilenamePart(value: string) {
  return value
    .trim()
    .replace(/[^\p{L}\p{N}_-]+/gu, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60) || 'tickets'
}

export function DebugTicketsExportTab() {
  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [channels, setChannels] = useState<ChannelRow[]>([])
  const [projectId, setProjectId] = useState('')
  const [channelId, setChannelId] = useState('')
  const [dateFrom, setDateFrom] = useState(daysAgoIsoDate(7))
  const [dateTo, setDateTo] = useState(todayIsoDate())
  const [format, setFormat] = useState<'csv' | 'json'>('csv')
  const [limit, setLimit] = useState<number | null>(null)
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [loadingChannels, setLoadingChannels] = useState(false)
  const [busy, setBusy] = useState(false)

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true)
    try {
      const rows = await api.projects()
      setProjects(rows)
      if (!projectId && rows[0]) setProjectId(rows[0].id)
    } catch (e) {
      showNotification({
        title: 'Проекты',
        message: e instanceof ApiError ? e.message : 'Не удалось загрузить проекты',
        color: 'red',
      })
    } finally {
      setLoadingProjects(false)
    }
  }, [projectId])

  const loadChannels = useCallback(async (pid: string) => {
    if (!pid) {
      setChannels([])
      setChannelId('')
      return
    }
    setLoadingChannels(true)
    try {
      const rows = await api.channelsList(pid)
      setChannels(rows)
      setChannelId((current) => {
        if (current && rows.some((row) => row.id === current)) return current
        return rows[0]?.id || ''
      })
    } catch (e) {
      showNotification({
        title: 'Каналы',
        message: e instanceof ApiError ? e.message : 'Не удалось загрузить каналы',
        color: 'red',
      })
    } finally {
      setLoadingChannels(false)
    }
  }, [])

  useEffect(() => {
    void loadProjects()
  }, [loadProjects])

  useEffect(() => {
    void loadChannels(projectId)
  }, [loadChannels, projectId])

  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: p.name || p.slug || p.id })),
    [projects],
  )

  const channelOptions = useMemo(
    () =>
      channels.map((c) => ({
        value: c.id,
        label: `${c.name || c.id} · ${c.channel_type}${c.is_active === false ? ' (выключен)' : ''}`,
      })),
    [channels],
  )

  const exportPreview = useExportPreview(
    channelId && dateFrom && dateTo
      ? () => api.exportChannelTicketsPreview(channelId, { date_from: dateFrom, date_to: dateTo, limit: limit ?? undefined })
      : null,
    [channelId, dateFrom, dateTo, limit],
  )
  const exportBlocked = !!exportPreview.error

  const exportTickets = useCallback(async () => {
    if (!channelId) {
      showNotification({ title: 'Канал не выбран', message: 'Выберите канал.', color: 'yellow' })
      return
    }
    if (!dateFrom || !dateTo) {
      showNotification({ title: 'Период не указан', message: 'Укажите обе даты.', color: 'yellow' })
      return
    }
    const channel = channels.find((row) => row.id === channelId)
    const project = projects.find((row) => row.id === projectId)
    setBusy(true)
    try {
      const blob = await api.exportChannelTickets(channelId, {
        date_from: dateFrom,
        date_to: dateTo,
        format,
        limit: limit ?? undefined,
      })
      const filename = [
        'tickets',
        safeFilenamePart(project?.name || project?.slug || 'project'),
        safeFilenamePart(channel?.name || channel?.channel_type || 'channel'),
        dateFrom,
        dateTo,
      ].join('-') + `.${format}`
      downloadBlob(blob, filename)
      showNotification({
        title: 'Выгрузка готова',
        message: `${format.toUpperCase()}-файл с тикетами скачан.`,
        color: 'green',
      })
    } catch (e) {
      showNotification({
        title: 'Выгрузка тикетов',
        message: e instanceof ApiError ? e.message : 'Не удалось выгрузить тикеты',
        color: 'red',
      })
    } finally {
      setBusy(false)
    }
  }, [channelId, channels, dateFrom, dateTo, format, limit, projectId, projects])

  return (
    <Stack gap="sm">
      <Alert color="blue" variant="light">
        <Text size="sm">
          Выгрузка содержит цельные тикеты: ссылка, тема, служебные поля и вся переписка без клиентских персональных данных.
          Без лимита выгружаются все тикеты за период.
        </Text>
      </Alert>

      <Card withBorder padding="sm" radius="md">
        <Group align="end" wrap="wrap">
          <NativeSelect
            label="Проект"
            value={projectId}
            data={projectOptions}
            disabled={loadingProjects || busy}
            onChange={(e) => setProjectId(e.currentTarget.value)}
            w={260}
          />
          <NativeSelect
            label="Канал"
            value={channelId}
            data={channelOptions.length ? channelOptions : [{ value: '', label: 'Каналов нет' }]}
            disabled={loadingChannels || busy || channelOptions.length === 0}
            onChange={(e) => setChannelId(e.currentTarget.value)}
            w={300}
          />
          <TextInput
            label="С даты"
            type="date"
            value={dateFrom}
            disabled={busy}
            onChange={(e) => setDateFrom(e.currentTarget.value)}
            w={150}
          />
          <TextInput
            label="По дату"
            type="date"
            value={dateTo}
            disabled={busy}
            onChange={(e) => setDateTo(e.currentTarget.value)}
            w={150}
          />
          <SegmentedControl
            value={format}
            onChange={(value) => setFormat(value as 'csv' | 'json')}
            data={[
              { value: 'csv', label: 'CSV' },
              { value: 'json', label: 'JSON' },
            ]}
            disabled={busy}
          />
          <Button
            leftSection={<IconDownload size={14} />}
            loading={busy}
            disabled={!channelId || exportBlocked}
            onClick={() => void exportTickets()}
          >
            Выгрузить
          </Button>
        </Group>
        <Stack mt="sm" gap="xs">
          <ExportLimitField value={limit} onChange={setLimit} disabled={busy} />
          <ExportPreviewNote {...exportPreview} />
        </Stack>
      </Card>
    </Stack>
  )
}
